import type { AutocompleteProps } from '@magicschool/spellbook/components/Autocomplete';
import { Autocomplete } from '@magicschool/spellbook/components/Autocomplete';
import type { CheckboxProps } from '@magicschool/spellbook/components/Checkbox';
import { Checkbox } from '@magicschool/spellbook/components/Checkbox';
import type { CheckboxGroupProps } from '@magicschool/spellbook/components/CheckboxGroup';
import { CheckboxGroup } from '@magicschool/spellbook/components/CheckboxGroup';
import type { DateInputProps } from '@magicschool/spellbook/components/DateInput';
import { DateInput } from '@magicschool/spellbook/components/DateInput';
import { Icon } from '@magicschool/spellbook/components/Icon';
import { IconButton } from '@magicschool/spellbook/components/IconButton';
import { Label } from '@magicschool/spellbook/components/Label';
import type { MultiSelectProps } from '@magicschool/spellbook/components/MultiSelect';
import { MultiSelect } from '@magicschool/spellbook/components/MultiSelect';
import type { NumberInputProps } from '@magicschool/spellbook/components/NumberInput';
import { NumberInput } from '@magicschool/spellbook/components/NumberInput';
import type { RadioGroupProps } from '@magicschool/spellbook/components/RadioGroup';
import { RadioGroup } from '@magicschool/spellbook/components/RadioGroup';
import type { SearchInputProps } from '@magicschool/spellbook/components/SearchInput';
import { SearchInput } from '@magicschool/spellbook/components/SearchInput';
import type { SelectProps } from '@magicschool/spellbook/components/Select';
import { Select } from '@magicschool/spellbook/components/Select';
import type { SlotComponent, SlotOptions, SlotProps } from '@magicschool/spellbook/components/Slot';
import { Slot } from '@magicschool/spellbook/components/Slot';
import type { SwitchProps } from '@magicschool/spellbook/components/Switch';
import { Switch } from '@magicschool/spellbook/components/Switch';
import type { SwitchGroupProps } from '@magicschool/spellbook/components/SwitchGroup';
import { SwitchGroup } from '@magicschool/spellbook/components/SwitchGroup';
import { Text } from '@magicschool/spellbook/components/Text';
import type { TextareaProps } from '@magicschool/spellbook/components/Textarea';
import { Textarea } from '@magicschool/spellbook/components/Textarea';
import type { TextInputProps } from '@magicschool/spellbook/components/TextInput';
import { TextInput } from '@magicschool/spellbook/components/TextInput';
import { Tooltip } from '@magicschool/spellbook/components/Tooltip';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { ComponentProps, ComponentType, ReactNode } from 'react';

export type DescriptionPosition = 'above-control' | 'below-control';

/** Props `Form.Group` passes to whichever control it detects among its children. */
export type FormControlProps = {
  id?: string;
  name?: string;
  required?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  labelId?: string;
  'aria-describedby'?: string;
};

type ForwardedSlots<T> = Omit<T, keyof ComponentType<unknown> | 'displayName' | '__docgenInfo' | '$typeof'>;

type FormControlSlotOptions<TSlotProps> = SlotOptions<TSlotProps> & {
  /**
   * The control is a `<div>` of labelled options (CheckboxGroup, RadioGroup, SwitchGroup). `Form.Label` then names it
   * through `labelId` rather than `htmlFor`, and helper text defaults to above it.
   */
  isGroup?: boolean;
};

/**
 * A control slot that also re-exposes the base component's own slots, so
 * `<Form.TextInput><Form.TextInput.LeadingAdornment /></Form.TextInput>` works without Form
 * listing them.
 */
function FormControlSlot<TSlotProps extends FormControlProps, TBaseComponent>(
  baseComponent: TBaseComponent,
  { isGroup = false, ...options }: FormControlSlotOptions<TSlotProps>,
) {
  const slot = Slot<TSlotProps>({ preventOverride: true, ...options });
  // Compound members are the PascalCase keys (`TextInput.LeadingAdornment`); a forwardRef's own `render` is not.
  const forwarded = Object.fromEntries(
    Object.entries(baseComponent as object).filter(([key, value]) => /^[A-Z]/.test(key) && typeof value === 'function'),
  );
  return Object.assign(slot, forwarded, { isGroup }) as SlotComponent<SlotProps<TSlotProps>> &
    ForwardedSlots<TBaseComponent> & { isGroup: boolean };
}

export type FormLabelSlotProps = {
  /** Set by `Form.Group`; leave unset */
  id?: string;
  /** Set by `Form.Group` to the control's `id`; leave unset */
  htmlFor?: string;
  /** Set by `Form.Group` from its own `required`; leave unset */
  required?: boolean;
  /** The visible label text */
  children?: ReactNode;
  /** Classes merged onto the label */
  className?: string;
};

export const FormLabelSlot = Slot<FormLabelSlotProps>({
  displayName: 'Form.Label',
  renderDefault: (props) => <Label data-spellbook-name="form-label" {...props} />,
});

export type FormTooltipSlotProps = {
  /** Tooltip content */
  children: ReactNode;
  /** Classes merged onto the info button */
  className?: string;
  /** Configuration for component default text content, labels, placeholders, and screen reader announcements. */
  text?: {
    /** Accessible name of the info button. Defaults to 'More information'. */
    trigger?: string;
  };
};

export const FormTooltipSlot = Slot<FormTooltipSlotProps>({
  displayName: 'Form.Tooltip',
  spellbookName: false,
  renderDefault: ({ children, text: { trigger = 'More information' } = {}, ...props }) => (
    <Tooltip>
      <Tooltip.Trigger>
        <IconButton size="xs" variant="tooltip" shape="circle" name="info" label={trigger} {...props} />
      </Tooltip.Trigger>
      <Tooltip.Content>{children}</Tooltip.Content>
    </Tooltip>
  ),
});

export type FormHelperTextSlotProps = {
  /** Set by `Form.Group` and added to the control's `aria-describedby`; leave unset */
  id?: string;
  /** Classes merged onto the text */
  className?: string;
  /** Places the helper text above or below the control, overriding the control's default */
  position?: DescriptionPosition;
  /** The helper text. Renders nothing when empty */
  children?: ReactNode;
};

export const FormHelperTextSlot = Slot<FormHelperTextSlotProps>({
  displayName: 'Form.HelperText',
  renderDefault: ({ children, className, position, ...props }) =>
    children ? (
      <Text data-spellbook-name="form-helper-text" color="default-soft" element="div" className={cn('text-left', className)} {...props}>
        {children}
      </Text>
    ) : null,
});

export type FormErrorMessageSlotProps = {
  /** Set by `Form.Group` and added to the control's `aria-describedby`; leave unset */
  id?: string;
  /** Classes merged onto the message row */
  className?: string;
  /** The error text. Renders nothing when empty, so it can stay mounted while the field is valid */
  children?: ReactNode;
  /** Icon name to display with the error message */
  icon?: ComponentProps<typeof Icon>['name'];
};

export const FormErrorMessageSlot = Slot<FormErrorMessageSlotProps>({
  displayName: 'Form.ErrorMessage',
  renderDefault: ({ children, className, icon = 'ban', ...props }) =>
    children ? (
      <div data-spellbook-name="form-error-message" className={cn('flex items-center gap-1', className)} aria-live="polite" {...props}>
        <Icon name={icon} size="sm" className="text-error-text flex-shrink-0" />
        <Text color="error">{children}</Text>
      </div>
    ) : null,
});

export type FormFooterTextSlotProps = {
  /** Set by `Form.Footer` and added to the control's `aria-describedby`; leave unset */
  id?: string;
  /** Classes merged onto the text */
  className?: string;
  /** Text describing the control, such as a character count. Renders nothing when empty */
  children?: ReactNode;
};

export const FormFooterTextSlot = Slot<FormFooterTextSlotProps>({
  displayName: 'Form.Footer.Text',
  renderDefault: ({ children, ...props }) =>
    children ? (
      <Text data-spellbook-name="form-footer-text" color="default-soft" {...props}>
        {children}
      </Text>
    ) : null,
});

export type FormFooterSlotProps = {
  /** Set by `Form.Group`; the id `Form.Footer.Text` receives. Leave unset */
  footerTextId?: string;
  /** Classes merged onto the footer row */
  className?: string;
  /** Buttons, counters, and an optional `Form.Footer.Text` */
  children?: ReactNode;
};

const FormFooterSlotBase = Slot<FormFooterSlotProps>({
  displayName: 'Form.Footer',
  renderDefault: ({ footerTextId, children, className, ...props }) => {
    const { slots, rest } = useSlots(children, {
      text: { Component: FormFooterTextSlot, props: { id: footerTextId } },
    });

    if (!children) {
      return null;
    }

    return (
      <div data-spellbook-name="form-footer" className={cn('flex items-center justify-end gap-2', className)} {...props}>
        {slots.text}
        {rest}
      </div>
    );
  },
});

export const FormFooterSlot = Object.assign(FormFooterSlotBase, {
  Text: FormFooterTextSlot,
});

type FormTextInputSlotProps = TextInputProps & FormControlProps;

export const FormTextInputSlot = FormControlSlot<FormTextInputSlotProps, typeof TextInput>(TextInput, {
  displayName: 'Form.TextInput',
  renderDefault: (props) => <TextInput data-spellbook-name="form-text-input" {...props} />,
});

type FormSelectSlotProps = SelectProps & FormControlProps;

export const FormSelectSlot = FormControlSlot<FormSelectSlotProps, typeof Select>(Select, {
  displayName: 'Form.Select',
  renderDefault: (props) => <Select data-spellbook-name="form-select" {...props} />,
});

type FormTextareaSlotProps = TextareaProps & FormControlProps;

export const FormTextareaSlot = FormControlSlot<FormTextareaSlotProps, typeof Textarea>(Textarea, {
  displayName: 'Form.Textarea',
  renderDefault: (props) => <Textarea data-spellbook-name="form-textarea" {...props} />,
});

type FormDateInputSlotProps = DateInputProps & FormControlProps;

export const FormDateInputSlot = FormControlSlot<FormDateInputSlotProps, typeof DateInput>(DateInput, {
  displayName: 'Form.DateInput',
  renderDefault: (props) => <DateInput data-spellbook-name="form-date-input" {...props} />,
});

type FormNumberInputSlotProps = NumberInputProps & FormControlProps;

export const FormNumberInputSlot = FormControlSlot<FormNumberInputSlotProps, typeof NumberInput>(NumberInput, {
  displayName: 'Form.NumberInput',
  renderDefault: (props) => <NumberInput data-spellbook-name="form-number-input" {...props} />,
});

type FormSearchInputSlotProps = SearchInputProps & FormControlProps;

export const FormSearchInputSlot = FormControlSlot<FormSearchInputSlotProps, typeof SearchInput>(SearchInput, {
  displayName: 'Form.SearchInput',
  renderDefault: (props) => <SearchInput data-spellbook-name="form-search-input" {...props} />,
});

type FormAutocompleteSlotProps = AutocompleteProps & FormControlProps;

export const FormAutocompleteSlot = FormControlSlot<FormAutocompleteSlotProps, typeof Autocomplete>(Autocomplete, {
  displayName: 'Form.Autocomplete',
  renderDefault: (props) => <Autocomplete data-spellbook-name="form-autocomplete" {...props} />,
});

type FormMultiSelectSlotProps = MultiSelectProps & FormControlProps;

export const FormMultiSelectSlot = FormControlSlot<FormMultiSelectSlotProps, typeof MultiSelect>(MultiSelect, {
  displayName: 'Form.MultiSelect',
  renderDefault: (props) => <MultiSelect data-spellbook-name="form-multi-select" {...props} />,
});

type FormCheckboxSlotProps = CheckboxProps & FormControlProps;

export const FormCheckboxSlot = FormControlSlot<FormCheckboxSlotProps, typeof Checkbox>(Checkbox, {
  displayName: 'Form.Checkbox',
  // The group's error message takes the description's place while invalid.
  renderDefault: ({ labelId, description, ...props }) => (
    <Checkbox data-spellbook-name="form-checkbox" description={props.invalid ? undefined : description} {...props} />
  ),
});

type FormSwitchSlotProps = SwitchProps & FormControlProps;

export const FormSwitchSlot = FormControlSlot<FormSwitchSlotProps, typeof Switch>(Switch, {
  displayName: 'Form.Switch',
  renderDefault: ({ labelId, ...props }) => <Switch data-spellbook-name="form-switch" {...props} />,
});

type FormCheckboxGroupSlotProps = CheckboxGroupProps & FormControlProps;

export const FormCheckboxGroupSlot = FormControlSlot<FormCheckboxGroupSlotProps, typeof CheckboxGroup>(CheckboxGroup, {
  displayName: 'Form.CheckboxGroup',
  isGroup: true,
  renderDefault: (props) => <CheckboxGroup data-spellbook-name="form-checkbox-group" {...props} />,
});

type FormRadioGroupSlotProps = RadioGroupProps & FormControlProps;

export const FormRadioGroupSlot = FormControlSlot<FormRadioGroupSlotProps, typeof RadioGroup>(RadioGroup, {
  displayName: 'Form.RadioGroup',
  isGroup: true,
  renderDefault: (props) => <RadioGroup data-spellbook-name="form-radio-group" {...props} />,
});

type FormSwitchGroupSlotProps = SwitchGroupProps & FormControlProps;

export const FormSwitchGroupSlot = FormControlSlot<FormSwitchGroupSlotProps, typeof SwitchGroup>(SwitchGroup, {
  displayName: 'Form.SwitchGroup',
  isGroup: true,
  renderDefault: (props) => <SwitchGroup data-spellbook-name="form-switch-group" {...props} />,
});

/** Every slot `Form.Group` treats as its control. */
export const FORM_CONTROL_SLOTS: ReadonlySet<ComponentType<never>> = new Set<ComponentType<never>>([
  FormTextInputSlot,
  FormSelectSlot,
  FormTextareaSlot,
  FormDateInputSlot,
  FormNumberInputSlot,
  FormSearchInputSlot,
  FormAutocompleteSlot,
  FormMultiSelectSlot,
  FormCheckboxSlot,
  FormSwitchSlot,
  FormCheckboxGroupSlot,
  FormRadioGroupSlot,
  FormSwitchGroupSlot,
]);
